/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useState } from 'react'
import { TitleWrap, TotalTabWrap } from '@styles/base.style'
import { Tabs } from 'antd-mobile'
import { Models } from 'rmc-tabs'
import useRootStore from '@store/useRootStore'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import { isMobile } from 'react-device-detect'
import { BackBtn, Button } from '@styles/button.style'
import { ArrowIco } from '@shared/Icon/ArrowIcon'
import { BettorFindRefreshBtn, SearchWrap } from '@views/BettorFind/index.style'
import { Input } from 'antd'
import { loadOrReload, showToast, TOAST_TYPE } from '@utils/utils'
import _ from 'lodash'
import { BETTOR_FIND_TYPE, GlobalStoreKey, NICKNAME_LENGTH } from '@v2/constants/common'
import MessageArrivalButton from '@components/Button/MessageArrivalButton'
import Chat from '@views/Popup/Chat'
import NavBar from '@views/Navbar'
import classNames from 'classnames'
import { BettorFindList } from './BettorFindList'
import PageResetIco from '../../shared/Icon/EtcIcon/PageResetIco'
import RankingPopupButton from '@views/Popup/RankingPopup/Button'

const { Search } = Input
let fetchBettorListTimer = null
let fetchBettorListReloadInterval = null

const BettorFind: React.FC = observer(() => {
  const { bettorStore, authStore, routerStore, chattingStore, userPickStore, logStore, globalStore } = useRootStore()
  const { isChatEnabled } = chattingStore
  const { isAndroidAppKeyboardVisible } = globalStore

  const [currentTabKey, setCurrentTabKey] = useState<BETTOR_FIND_TYPE>(() => {
    const options = globalStore.getBettorFindTabOptions()

    if (options.baseSearch.enabled) return BETTOR_FIND_TYPE.NEW_MEMBER
    if (options.todayBetting.enabled) return BETTOR_FIND_TYPE.TODAY_BETTOR
    if (options.customTab.enabled) return BETTOR_FIND_TYPE.CUSTOM_TAB
    if (options.betSuccess.enabled) return BETTOR_FIND_TYPE.BET_HIT_SUCCESS

    return BETTOR_FIND_TYPE.ALL_BETTOR
  })
  const [searchNick, setSearchNick] = useState('')
  const [bettorTabList] = useState<Models.TabData[]>(() => {
    const list: Models.TabData[] = []
    const options = globalStore.getBettorFindTabOptions()

    if (options.baseSearch.enabled) {
      list.push({
        title: (
          <span className="tab-item large-txt">
            <span className="txt">신규가입</span>
          </span>
        ),
        name: '신규가입',
        key: BETTOR_FIND_TYPE.NEW_MEMBER.toString(),
      })
    }
    if (options.todayBetting.enabled) {
      list.push({
        title: (
          <span className="tab-item large-txt">
            <span className="txt">오늘의 베터</span>
          </span>
        ),
        name: '오늘의 베터',
        key: BETTOR_FIND_TYPE.TODAY_BETTOR.toString(),
      })
    }
    if (options.customTab.enabled) {
      list.push({
        title: (
          <span className="tab-item large-txt">
            <span className="txt">{options.customTab.tabName || '커스텀 탭'}</span>
          </span>
        ),
        name: options.customTab.tabName || '커스텀 탭',
        key: BETTOR_FIND_TYPE.CUSTOM_TAB.toString(),
      })
    }
    if (options.betSuccess.enabled) {
      list.push({
        title: (
          <span className="tab-item large-txt">
            <span className="txt">적중</span>
          </span>
        ),
        name: '적중',
        key: BETTOR_FIND_TYPE.BET_HIT_SUCCESS.toString(),
      })
    }

    list.push({
      title: (
        <span className="tab-item large-txt bettor-find-all">
          <span className="txt">전체</span>
        </span>
      ),
      name: '전체',
      key: BETTOR_FIND_TYPE.ALL_BETTOR.toString(),
    })

    return list
  })

  useOnMount(() => {
    if (!authStore.isSignedIn) {
      loadOrReload('/')
    }

    fetchBettorList(true)
    restartReloadInterval()
  })

  useEffect(() => {
    if (currentTabKey !== BETTOR_FIND_TYPE.ALL_BETTOR) {
      setSearchNick('')
    }
    fetchBettorList(true)

    if(currentTabKey !== BETTOR_FIND_TYPE.ALL_BETTOR) {
      restartReloadInterval()
    } else {
      clearInterval(fetchBettorListReloadInterval)
    }

    setTimeout(() => {
      const element = document.querySelector(`#bettorListWrap`).children[0]
      if (element) element.scrollTop = 0
    }, 400)
  }, [currentTabKey])

  useOnUnmount(() => {
    clearInterval(fetchBettorListReloadInterval)
    userPickStore.resetUserPickMine()
    bettorStore.resetBettorFindState()
  })

  const restartReloadInterval = () => {
    const intervalMinute = _.toNumber(
      globalStore.findGlobalSetting(GlobalStoreKey.BETTOR_FIND_REFRESH_INTERVAL_MINUTE_TIME)?.value ?? 0,
    )
    if (intervalMinute < 1) {
      return
    }
    clearInterval(fetchBettorListReloadInterval)
    fetchBettorListReloadInterval = setInterval(() => {
      bettorStore.fetchBettors(currentTabKey, searchNick, true).catch(console.error)
    }, intervalMinute * 60 * 1000)
  }

  const clearReloadInterval = () => {
    clearInterval(fetchBettorListReloadInterval)
  }

  const isClearButtonEvent = (value: string) => {
    return _.isEmpty(_.trim(value))
  }

  const fetchBettorList = (reset = false) => {
    clearTimeout(fetchBettorListTimer)
    fetchBettorListTimer = setTimeout(() => {
      bettorStore.fetchBettors(currentTabKey, searchNick, reset).catch(console.error)
    }, 200)
  }

  const validateSearchNickName = (nickName: string): boolean => {
    return !(_.isEmpty(_.trim(nickName)) || _.size(nickName) < NICKNAME_LENGTH.MIN)
  }

  const doClickSearchBtn = (
    value: string,
    event?: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    // 클리어 버튼을 이용한 경우는 토스트 메시지를 표시하지 않는다.
    if (_.isNil(event['key']) && event?.target['id'] === 'better_nick_search_input' && isClearButtonEvent(value)) {
      setSearchNick('')
      if (currentTabKey !== BETTOR_FIND_TYPE.NEW_MEMBER) {
        setCurrentTabKey(BETTOR_FIND_TYPE.NEW_MEMBER)
      } else {
        fetchBettorList(true)
      }
      return
    }

    if (validateSearchNickName(value)) {
      if (currentTabKey !== BETTOR_FIND_TYPE.ALL_BETTOR) {
        setCurrentTabKey(BETTOR_FIND_TYPE.ALL_BETTOR)
      } else {
        fetchBettorList(true)
      }
      return
    }

    showToast(TOAST_TYPE.WARN, '2글자 이상 입력해야 합니다.')
  }



  const onChange = (tab: Models.TabData) => {
    setCurrentTabKey(Number(tab.key) as BETTOR_FIND_TYPE)
  }

  const onChangeNickSearchField = event => {
    const value = event.target.value
    if (_.isEmpty(_.toString(value))) {
      restartReloadInterval()
    } else {
      clearReloadInterval()
    }
    setSearchNick(value)
  }

  const searchClass = classNames('white-search', {
    'white-search pc-search-wrap': !isMobile,
  })

  const onClickRefreshBtn = () => {
    fetchBettorList(true)
    restartReloadInterval()
  }

  return (
    <Fragment>
      {isMobile && (
        <TitleWrap className="title-wrap theme-txt-color">
          <BackBtn onClick={() => {
            logStore.writeButtonLog({ pageId: 'd050', buttonId: '001' }).catch(console.error)
            routerStore.goBack()
          }}>
            <ArrowIco/>
          </BackBtn>
        </TitleWrap>
      )}

      <SearchWrap className={searchClass}>
        <Search
          id="better_nick_search_input"
          value={searchNick}
          className="nick"
          placeholder="베터 닉네임을 검색하세요"
          enterButton="검색"
          onSearch={doClickSearchBtn}
          onChange={onChangeNickSearchField}
          allowClear
        />
      </SearchWrap>
      <TotalTabWrap whiteUnderline className="left-align bettor-find-tabs">
        <Tabs
          tabs={bettorTabList}
          page={currentTabKey.toString()}
          onChange={onChange}
        />
        {currentTabKey !== BETTOR_FIND_TYPE.ALL_BETTOR &&
          <Button TP noRadius onClick={onClickRefreshBtn} css={BettorFindRefreshBtn}>
            <PageResetIco/>
          </Button>}
      </TotalTabWrap>
      <BettorFindList bettorFindType={currentTabKey} searchNick={searchNick}
                      onAction={restartReloadInterval} onCancel={clearReloadInterval}/>

      <MessageArrivalButton key="Bettor:MessageArrivalButton"/>
      {isChatEnabled && !authStore.isSpecificClubMember && isMobile && <Chat key="BettorFind:Chat"/>}
      {/*TODO: 랭킹 현황을 볼 수 있는 팝업*/}
      <RankingPopupButton/>
      {isMobile && !isAndroidAppKeyboardVisible && <NavBar key="Bettor:NavBar"/>}
    </Fragment>
  )
})

export default BettorFind
